import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ActionMenuComponent } from '../../../../shared/components/action-menu/action-menu.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { DEFAULT_FILTER } from '../../../../shared/constants/constants';
import { FilterModel } from '../../../../shared/interface/interface';
import { AuthorModel } from '../../../../shared/models/author.model';
import { AuthorService } from '../../../../shared/services/author.service';
import { ScrollDisableService } from '../../../../shared/services/scroll-disable.service';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-authors',
  standalone: true,
  templateUrl: './authors.component.html',
  styleUrl: './authors.component.scss',
  imports: [
    SharedModule,
    PageHeaderComponent,
    SkeletonLoaderComponent,
    ActionMenuComponent,
    ConfirmationModalComponent,
  ],
})
export class AuthorsComponent implements OnInit, OnDestroy {
  isLoading = false;
  authors: AuthorModel[] = [];
  menuOpenIndex = -1;
  deleteAuthorId = '';
  deleteLoading = false;
  subscription: Subscription = new Subscription();
  totalAuthor = 0;
  private authorFilterSubject$: BehaviorSubject<FilterModel> =
    new BehaviorSubject<FilterModel>(DEFAULT_FILTER);
  authorFilter = this.authorFilterSubject$.asObservable();

  constructor(
    private scrollDisableService: ScrollDisableService,
    private router: Router,
    private authorService: AuthorService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscribeToAuthorFilter();
  }

  handleSearch(searchText: string) {
    if (searchText.length === 0 || searchText?.trim()) {
      this.updateAuthorFilter({
        ...this.authorFilterSubject$.value,
        pageNumber: 1,
        searchText: searchText,
      });
    }
  }

  loadMoreIfLastIndex() {
    const { searchText, pageNumber, limit } = this.authorFilterSubject$.value;
    if (pageNumber * limit < this.totalAuthor) {
      this.updateAuthorFilter({
        searchText,
        limit,
        pageNumber: pageNumber + 1,
      });
    }
  }

  closeAllMenu(index: number) {
    if (index === this.menuOpenIndex) {
      this.menuOpenIndex = -1;
    }
  }

  openMenu(index: number) {
    this.menuOpenIndex = index;
  }

  openDeleteAuthorModal(id: string) {
    this.deleteAuthorId = id;
    this.scrollDisableService.stopScroll();
  }

  handleEditAuthor(id: string) {
    this.router.navigate([`/edit-author/${id}`]);
  }

  cancelDelete() {
    this.deleteAuthorId = '';
    this.scrollDisableService.addScroll();
  }

  deleteAuthor() {
    this.deleteLoading = true;
    this.authorService.deleteAuthor(this.deleteAuthorId).subscribe({
      next: () => {
        this.toastr.success('Author deleted successfully!');
        this.deleteLoading = false;
        this.deleteAuthorId = '';
        this.authorFilterSubject$.next(DEFAULT_FILTER);
        this.scrollDisableService.addScroll();
      },
      error: (err) => {
        console.error(err);
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
      },
    });
  }

  subscribeToAuthorFilter() {
    const sub = this.authorFilter.subscribe((filter: FilterModel) => {
      this.getAuthorList(filter);
    });
    this.subscription.add(sub);
  }

  getAuthorList(filter: FilterModel) {
    this.isLoading = true;
    this.authorService.fetchAuthors(filter).subscribe({
      next: (res) => {
        this.authors =
          filter.pageNumber > 1 ? [...this.authors, ...res.result] : res.result;
        this.totalAuthor = res.count;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
        this.isLoading = false;
      },
    });
  }
  updateAuthorFilter(filter: FilterModel) {
    this.authorFilterSubject$.next(filter);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
